/**
 * Deterministic Russian reader for calendar sentences ("Работа завтра с 18:45 до 20:45").
 *
 * No AI and no statistics: a fixed list of patterns, applied in a fixed order, each on the text
 * that earlier ones left over. A recognised phrase is cut out of the title; everything else stays
 * as typed. What the grammar understands is listed in `docs/frontend.md` (CAL-5).
 *
 * Rules the reader never breaks:
 *  - nothing is invented: a missing end time, date or title is reported, not guessed;
 *  - nothing is corrected: 31 November or 25:00 are reported as invalid and stay in the text;
 *  - two different readings are reported as ambiguous, none is picked;
 *  - a derived value (the year of "25 октября", the end of an overnight event, the first repeat of
 *    "каждый вторник") is reported as `assumed`.
 *
 * Every pattern is a plain alternation of bounded pieces on text of at most 300 characters, and
 * the lists ("пн и ср") are walked by a loop, so no input can make it backtrack badly.
 */
import { addDays, isIsoDate } from '@/shared/lib/calendar';

import {
  END,
  MONTHS_GENITIVE,
  NUMBER,
  numberOf,
  START,
  UNSUPPORTED_WORDS,
  WEEKDAY_STEMS,
} from './lexicon';
import {
  MAX_INPUT_LENGTH,
  type ParseContext,
  type ParsedEvent,
  type ParsedRecurrence,
  type ParseNote,
  type RepeatFrequency,
} from './types';

export const MASK = '\u0001';
const MONTHS = MONTHS_GENITIVE.join('|');
export const WD = `(?:${WEEKDAY_STEMS.join('|')})[а-яё]{0,4}`;
const DATE_TEXT = `(\\d{1,2})\\s+(${MONTHS})(?:\\s+(\\d{4}))?`;
const DATE_DOTTED = `(\\d{1,2})\\.(0?[1-9]|1[0-2])(?:\\.(\\d{4}))?`;
const TIME = '(\\d{1,2}(?::\\d{2})?)';
const UNIT = `(?:(?:\\d{1,3}|${NUMBER.slice(1, -1).split('|').slice(1).join('|')})\\s+)?(?:час(?:а|ов)?|минут[уыа]?)`;

export const re = (source: string, flags = 'u') => new RegExp(source, flags);
const sticky = (source: string) => re(source, 'uy');

export class Text {
  readonly original: string;
  work: string;
  constructor(original: string) {
    this.original = original;
    // Lower case char by char so that positions never shift.
    this.work = Array.from(original, (char) => {
      const lower = char.toLowerCase();
      return lower.length === char.length ? lower : char;
    }).join('');
  }

  find(pattern: RegExp): RegExpExecArray | null {
    return pattern.exec(this.work);
  }

  /** Cuts the matched phrase out of the text (it can no longer be matched, nor is it in the title). */
  consume(match: RegExpExecArray): void {
    const end = match.index + match[0].length;
    this.work =
      this.work.slice(0, match.index) + MASK.repeat(end - match.index) + this.work.slice(end);
  }

  /** Cuts a span by position (for phrases found by code rather than by one pattern). */
  cut(start: number, end: number): void {
    this.work = this.work.slice(0, start) + MASK.repeat(end - start) + this.work.slice(end);
  }

  take(pattern: RegExp): RegExpExecArray | null {
    const match = this.find(pattern);
    if (match) this.consume(match);
    return match;
  }

  title(): string {
    let result = '';
    for (let index = 0; index < this.original.length; index++) {
      result += this.work[index] === MASK ? ' ' : (this.original[index] ?? '');
    }
    const joined = result.replace(/\s+/gu, ' ').trim();
    // A preposition or a stray separator left at an edge belonged to the removed phrase.
    return joined
      .replace(/^(?:[\s,;:–—-]|(?:в|во|на|с|со|по|до|к)(?=\s))+/iu, '')
      .replace(/(?:[\s,;:–—-]|(?<=\s)(?:в|во|на|с|со|по|до|к))+$/iu, '')
      .trim();
  }
}

export const weekdayIndex = (word: string): number =>
  WEEKDAY_STEMS.findIndex((stem) => word.startsWith(stem));

export function isoWeekdayIndex(date: string): number {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

export function makeDate(year: number, month: number, day: number): string | null {
  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return isIsoDate(iso) ? iso : null;
}

const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/** The first date of `weekday` (0 = Monday) strictly AFTER `today`. */
export function nextWeekday(today: string, weekday: number): string {
  const ahead = (weekday - isoWeekdayIndex(today) + 7) % 7 || 7;
  return addDays(today, ahead);
}

/** The date of `weekday` in the calendar week (Monday - Sunday) AFTER the one holding `today`. */
export function weekdayOfNextWeek(today: string, weekday: number): string {
  const monday = addDays(today, -isoWeekdayIndex(today));
  return addDays(monday, 7 + weekday);
}

/** Walks "пн, ср и пт": the first weekday is already read, the rest are read one by one. */
function weekdayList(text: Text, first: string, from: number): number[] {
  const days = [weekdayIndex(first)];
  const next = sticky(`\\s*(?:,|и)\\s*(${WD})(?![\\p{L}\\p{N}])`);
  let at = from;
  for (let step = 0; step < 6; step++) {
    next.lastIndex = at;
    const found = next.exec(text.work);
    if (!found) break;
    text.consume(found);
    days.push(weekdayIndex(found[1] ?? ''));
    at = found.index + found[0].length;
  }
  return [...new Set(days)].sort((a, b) => a - b);
}

interface Recurrence {
  frequency: RepeatFrequency;
  interval: number;
  weekdays: number[];
}

function readRecurrence(text: Text): Recurrence | null {
  let m: RegExpExecArray | null;
  const wdTail = (from: number): number[] => {
    const tail = sticky(`\\s*(?:по|в|во)\\s+(${WD})(?![\\p{L}\\p{N}])`);
    tail.lastIndex = from;
    const found = tail.exec(text.work);
    if (!found) return [];
    text.consume(found);
    return weekdayList(text, found[1] ?? '', found.index + found[0].length);
  };
  // "каждые две недели по средам", "каждую неделю"
  m = text.take(re(`${START}кажд(?:ые|ую)\\s+${NUMBER}\\s+недел[а-яё]{1,3}${END}`));
  if (m) {
    return {
      frequency: 'weekly',
      interval: numberOf(m[1] ?? '1'),
      weekdays: wdTail(m.index + m[0].length),
    };
  }
  m = text.take(re(`${START}(?:кажд(?:ую|ая)\\s+неделю|еженедельно)${END}`));
  if (m) return { frequency: 'weekly', interval: 1, weekdays: wdTail(m.index + m[0].length) };
  // "каждый вторник и четверг", "по средам"
  m = text.take(re(`${START}(?:кажд(?:ый|ую|ое)|по)\\s+(${WD})${END}`));
  if (m) {
    return {
      frequency: 'weekly',
      interval: 1,
      weekdays: weekdayList(text, m[1] ?? '', m.index + m[0].length),
    };
  }
  if (text.take(re(`${START}по\\s+будням${END}`))) {
    return { frequency: 'weekly', interval: 1, weekdays: [0, 1, 2, 3, 4] };
  }
  if (text.take(re(`${START}по\\s+выходным${END}`))) {
    return { frequency: 'weekly', interval: 1, weekdays: [5, 6] };
  }
  m = text.take(re(`${START}кажд(?:ые|ых)\\s+${NUMBER}\\s+(?:дня|дней)${END}`));
  if (m) return { frequency: 'daily', interval: numberOf(m[1] ?? '1'), weekdays: [] };
  if (text.take(re(`${START}(?:ежедневно|кажд(?:ый|ые)\\s+день)${END}`))) {
    return { frequency: 'daily', interval: 1, weekdays: [] };
  }
  m = text.take(re(`${START}кажд(?:ые|ых)\\s+${NUMBER}\\s+месяц[а-яё]*${END}`));
  if (m) return { frequency: 'monthly', interval: numberOf(m[1] ?? '1'), weekdays: [] };
  if (text.take(re(`${START}(?:ежемесячно|каждый\\s+месяц)${END}`))) {
    return { frequency: 'monthly', interval: 1, weekdays: [] };
  }
  m = text.take(re(`${START}кажд(?:ые|ых)\\s+${NUMBER}\\s+(?:года|лет)${END}`));
  if (m) return { frequency: 'yearly', interval: numberOf(m[1] ?? '1'), weekdays: [] };
  if (text.take(re(`${START}(?:ежегодно|каждый\\s+год)${END}`))) {
    return { frequency: 'yearly', interval: 1, weekdays: [] };
  }
  return null;
}

type EndRule =
  | { kind: 'month'; month: number }
  | { kind: 'year' }
  | { kind: 'date'; day: number; month: number; year: number | null };

function readEndRule(text: Text): EndRule | null {
  let m = text.take(re(`${START}до\\s+конца\\s+(${MONTHS})${END}`));
  if (m)
    return {
      kind: 'month',
      month: MONTHS_GENITIVE.indexOf(m[1] as (typeof MONTHS_GENITIVE)[number]) + 1,
    };
  if (text.take(re(`${START}до\\s+конца\\s+года${END}`))) return { kind: 'year' };
  m = text.take(re(`${START}до\\s+${DATE_TEXT}${END}`));
  if (m) {
    return {
      kind: 'date',
      day: Number(m[1]),
      month: MONTHS_GENITIVE.indexOf(m[2] as (typeof MONTHS_GENITIVE)[number]) + 1,
      year: m[3] ? Number(m[3]) : null,
    };
  }
  m = text.take(re(`${START}до\\s+${DATE_DOTTED}${END}`));
  if (m)
    return {
      kind: 'date',
      day: Number(m[1]),
      month: Number(m[2]),
      year: m[3] ? Number(m[3]) : null,
    };
  return null;
}

interface Found {
  iso: string;
  year: 'given' | 'assumed' | 'n/a';
}

export const clock = (hour: number, minute: number) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

/** `18:45`, `9`, with an optional "утра / дня / вечера / ночи" part: the time or null if impossible. */
function readClock(token: string, period?: string): string | null {
  const [hourText = '', minuteText] = token.split(':');
  let hour = Number(hourText);
  const minute = minuteText === undefined ? 0 : Number(minuteText);
  if (!Number.isInteger(hour) || minute > 59) return null;
  if (period === 'вечера' && hour >= 1 && hour < 12) hour += 12;
  else if (period === 'дня' && hour >= 1 && hour < 12) hour += hour === 12 ? 0 : 12;
  else if (period === 'ночи' && hour === 12) hour = 0;
  if (hour > 23) return null;
  return clock(hour, minute);
}

/**
 * "24:00" (or a bare "24") names the END of the day: the next day's 00:00. It is accepted only as
 * the end of a range ("с 15:00 до 24:00"); as a start or a lone time 24 stays an invalid hour.
 */
export const isMidnightEnd = (token: string): boolean => /^24(?::00)?$/.test(token);

export function parseEvent(input: string, context: ParseContext): ParsedEvent {
  const notes: ParseNote[] = [];
  const note = (kind: ParseNote['kind'], code: string, params?: ParseNote['params']) => {
    notes.push({ kind, code, ...(params && { params }) });
  };
  const empty: ParsedEvent = {
    input,
    title: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    allDay: false,
    durationMinutes: null,
    recurrence: null,
    notes,
    complete: false,
  };
  if (input.length > MAX_INPUT_LENGTH) {
    note('invalid', 'tooLong', { max: MAX_INPUT_LENGTH });
    return empty;
  }
  const today = context.today;
  const year = Number(today.slice(0, 4));
  const text = new Text(input);

  // --- repetition and its end ----------------------------------------------------------------
  const repeat = readRecurrence(text);
  const endRule = readEndRule(text);
  if (endRule && !repeat) note('unsupported', 'untilWithoutRepeat');

  // --- dates ---------------------------------------------------------------------------------
  const found: Found[] = [];
  const explicit = (day: number, month: number, given: number | null) => {
    if (given !== null) {
      const iso = makeDate(given, month, day);
      if (iso) found.push({ iso, year: 'given' });
      else
        note('invalid', 'invalidDate', {
          value: `${String(day)}.${String(month)}.${String(given)}`,
        });
      return;
    }
    let iso = makeDate(year, month, day);
    if (iso && iso < today) {
      iso = makeDate(year + 1, month, day);
      if (iso) found.push({ iso, year: 'assumed' });
    } else if (iso) {
      found.push({ iso, year: 'n/a' });
    }
    if (!iso) note('invalid', 'invalidDate', { value: `${String(day)}.${String(month)}` });
  };
  for (let step = 0; step < 3; step++) {
    const m = text.take(re(`${START}${DATE_TEXT}${END}`));
    if (!m) break;
    explicit(
      Number(m[1]),
      MONTHS_GENITIVE.indexOf(m[2] as (typeof MONTHS_GENITIVE)[number]) + 1,
      m[3] ? Number(m[3]) : null,
    );
  }
  for (let step = 0; step < 3; step++) {
    const m = text.take(re(`${START}${DATE_DOTTED}${END}`));
    if (!m) break;
    explicit(Number(m[1]), Number(m[2]), m[3] ? Number(m[3]) : null);
  }
  const relative = text.take(re(`${START}(сегодня|завтра|послезавтра)${END}`));
  if (relative) {
    const offset = { сегодня: 0, завтра: 1, послезавтра: 2 }[relative[1] as 'сегодня'];
    found.push({ iso: addDays(today, offset), year: 'n/a' });
  }
  const weekdayWord = text.take(
    re(`${START}(?:в|во)\\s+(?:(следующ[а-яё]{2,3})\\s+)?(${WD})${END}`),
  );
  let weekday: number | null = null;
  let weekdayDate = '';
  if (weekdayWord) {
    weekday = weekdayIndex(weekdayWord[2] ?? '');
    const iso = weekdayWord[1] ? weekdayOfNextWeek(today, weekday) : nextWeekday(today, weekday);
    weekdayDate = iso;
  }
  // "каждый месяц 15 числа": the day of a monthly series
  let monthDay: number | null = null;
  if (repeat?.frequency === 'monthly') {
    const m = text.take(re(`${START}(\\d{1,2})(?:-?го)?\\s+числа${END}`));
    if (m) monthDay = Number(m[1]);
  }

  // --- time of day ---------------------------------------------------------------------------
  let startTime = '';
  let endTime = '';
  let explicitEnd = false;
  let midnightEnd = false;
  const range =
    text.find(re(`${START}(?:с|со)\\s+${TIME}\\s+(?:до|по)\\s+${TIME}${END}`)) ??
    text.find(re(`${START}(\\d{1,2}:\\d{2})\\s*[-–—]\\s*(\\d{1,2}:\\d{2})${END}`));
  if (range) {
    const from = readClock(range[1] ?? '');
    midnightEnd = isMidnightEnd(range[2] ?? '');
    const to = midnightEnd ? '00:00' : readClock(range[2] ?? '');
    text.consume(range);
    if (from && to) {
      [startTime, endTime, explicitEnd] = [from, to, true];
    } else {
      note('invalid', 'invalidTime', { value: !from ? (range[1] ?? '') : (range[2] ?? '') });
      midnightEnd = false;
    }
  }
  const single =
    text.find(re(`${START}(?:в|к)\\s+(\\d{1,2}:\\d{2})${END}`)) ??
    text.find(re(`${START}(?:в|к)\\s+(\\d{1,2})\\s+(утра|дня|вечера|ночи|часов|часа|час)${END}`));
  if (single) {
    const period = ['утра', 'дня', 'вечера', 'ночи'].includes(single[2] ?? '')
      ? single[2]
      : undefined;
    const value = readClock(single[1] ?? '', period);
    if (!value) {
      note('invalid', 'invalidTime', { value: single[1] ?? '' });
    } else if (startTime && value !== startTime) {
      text.consume(single);
      note('ambiguous', 'twoTimes');
    } else {
      text.consume(single);
      startTime = value;
    }
  }
  const stray = text.find(re(`${START}\\d{1,2}:\\d{2}${END}`));
  if (stray) {
    // a second time next to a recognised one is a contradiction, an unreadable one is invalid
    if (startTime) note('ambiguous', 'twoTimes');
    else note('invalid', 'invalidTime', { value: stray[0] });
  }

  // --- duration ------------------------------------------------------------------------------
  let duration: number | null = null;
  const durationText = text.find(
    re(`${START}на\\s+(полчаса|полтора\\s+часа|полторы\\s+часа|(?:${UNIT})(?:\\s+${UNIT})?)${END}`),
  );
  if (durationText) {
    text.consume(durationText);
    const body = durationText[1] ?? '';
    if (body === 'полчаса') duration = 30;
    else if (body.startsWith('полтор')) duration = 90;
    else {
      duration = 0;
      const unit = re(`(?:(\\d{1,3}|${NUMBER.slice(1, -1)})\\s+)?(час|минут)`, 'gu');
      for (let step = 0; step < 2; step++) {
        const piece = unit.exec(body);
        if (!piece) break;
        const amount = piece[1] ? numberOf(piece[1]) : 1;
        duration += piece[2] === 'час' ? amount * 60 : amount;
      }
    }
    if (duration < 1 || duration > 1440) {
      note('invalid', 'invalidDuration');
      duration = null;
    }
  }

  // --- what is left of the sentence ----------------------------------------------------------
  for (const word of UNSUPPORTED_WORDS) {
    const left = text.find(re(`${START}${word}[а-яё]*${END}`));
    if (left) {
      const code =
        word.startsWith('кажд') || word.startsWith('еже')
          ? 'unsupportedRepeat'
          : 'unsupportedPhrase';
      note('unsupported', code, { word: left[0] });
      break;
    }
  }

  // --- the start date ------------------------------------------------------------------------
  // A weekday names the date only when nothing else does; next to a date it is just a check
  // ("завтра в пятницу" agrees only when tomorrow IS a Friday).
  if (weekdayDate && found.length === 0) found.push({ iso: weekdayDate, year: 'n/a' });
  const distinct = [...new Set(found.map((item) => item.iso))];
  let startDate = '';
  if (distinct.length > 1) {
    note('ambiguous', 'ambiguousDates');
  } else if (distinct.length === 1) {
    startDate = distinct[0] ?? '';
    if (found.some((item) => item.year === 'assumed'))
      note('assumed', 'assumedYear', { date: startDate });
    if (weekday !== null && isoWeekdayIndex(startDate) !== weekday) {
      note('ambiguous', 'weekdayMismatch');
    }
  }

  // --- recurrence ----------------------------------------------------------------------------
  let recurrence: ParsedRecurrence | null = null;
  if (repeat) {
    let weekdays = repeat.weekdays;
    if (repeat.frequency === 'weekly' && weekdays.length === 0) {
      if (startDate) weekdays = [isoWeekdayIndex(startDate)];
      else note('missing', 'repeatWeekday');
    }
    if (!startDate && distinct.length === 0) {
      if (repeat.frequency === 'weekly' && weekdays.length > 0) {
        let probe = today;
        for (let step = 0; step < 7 && !weekdays.includes(isoWeekdayIndex(probe)); step++)
          probe = addDays(probe, 1);
        startDate = probe;
        note('assumed', 'assumedFirstRepeat', { date: startDate });
      } else if (repeat.frequency === 'daily') {
        startDate = today;
        note('assumed', 'assumedFirstRepeat', { date: startDate });
      } else if (repeat.frequency === 'monthly' && monthDay !== null) {
        for (let step = 0; step < 13 && !startDate; step++) {
          const base = new Date(Date.UTC(year, Number(today.slice(5, 7)) - 1 + step, 1));
          const candidate = makeDate(base.getUTCFullYear(), base.getUTCMonth() + 1, monthDay);
          if (
            candidate &&
            candidate >= today &&
            monthDay <= daysInMonth(base.getUTCFullYear(), base.getUTCMonth() + 1)
          ) {
            startDate = candidate;
          }
        }
        if (startDate) note('assumed', 'assumedFirstRepeat', { date: startDate });
        else note('invalid', 'invalidDate', { value: String(monthDay) });
      }
    } else if (startDate && repeat.frequency === 'weekly' && weekdays.length > 0) {
      if (!weekdays.includes(isoWeekdayIndex(startDate))) note('ambiguous', 'repeatDateMismatch');
    }
    if (monthDay !== null && startDate && Number(startDate.slice(8, 10)) !== monthDay) {
      note('ambiguous', 'repeatDateMismatch');
    }
    if (!startDate && distinct.length === 0 && !notes.some((n) => n.code === 'repeatWeekday')) {
      note('missing', 'missingDate');
    }
    // the end of the SERIES (never the end time of one event)
    let untilDate = '';
    if (endRule) {
      const base = startDate || today;
      const baseYear = Number(base.slice(0, 4));
      if (endRule.kind === 'year') {
        untilDate = makeDate(baseYear, 12, 31) ?? '';
      } else if (endRule.kind === 'month') {
        let candidate = makeDate(baseYear, endRule.month, daysInMonth(baseYear, endRule.month));
        if (candidate && candidate < base)
          candidate = makeDate(
            baseYear + 1,
            endRule.month,
            daysInMonth(baseYear + 1, endRule.month),
          );
        untilDate = candidate ?? '';
      } else if (endRule.year !== null) {
        untilDate = makeDate(endRule.year, endRule.month, endRule.day) ?? '';
      } else {
        let candidate = makeDate(baseYear, endRule.month, endRule.day);
        if (candidate && candidate < base)
          candidate = makeDate(baseYear + 1, endRule.month, endRule.day);
        untilDate = candidate ?? '';
      }
      if (!untilDate)
        note('invalid', 'invalidDate', {
          value: String(endRule.kind === 'date' ? endRule.day : 0),
        });
      else if (startDate && untilDate < startDate) {
        note('invalid', 'untilBeforeStart');
        untilDate = '';
      }
    }
    recurrence = { frequency: repeat.frequency, interval: repeat.interval, weekdays, untilDate };
  } else if (
    distinct.length === 0 &&
    !found.length &&
    !notes.some((n) => n.code === 'invalidDate')
  ) {
    note('missing', 'missingDate');
  }

  // --- the end ---------------------------------------------------------------------------------
  let endDate = '';
  if (startTime && duration !== null && !explicitEnd) {
    const [h = 0, mi = 0] = startTime.split(':').map(Number);
    const total = h * 60 + mi + duration;
    endTime = clock(Math.floor(total / 60) % 24, total % 60);
    endDate = startDate ? addDays(startDate, Math.floor(total / 1440)) : '';
    if (total >= 1440) note('assumed', 'assumedOvernight');
  } else if (explicitEnd && duration !== null) {
    note('ambiguous', 'durationConflict');
  }
  if (startTime && endTime && explicitEnd) {
    if (midnightEnd) {
      endDate = startDate ? addDays(startDate, 1) : '';
      note('assumed', 'assumedOvernight');
    } else if (endTime < startTime) {
      endDate = startDate ? addDays(startDate, 1) : '';
      note('assumed', 'assumedOvernight');
    } else if (endTime === startTime) {
      note('invalid', 'emptyInterval');
      endTime = '';
    } else {
      endDate = startDate;
    }
  } else if (startTime && endTime && !explicitEnd && !endDate) {
    endDate = startDate;
  }
  if (startTime && !endTime && !notes.some((n) => n.code === 'invalidDuration'))
    note('missing', 'missingEnd');
  if (!startTime && duration !== null) note('missing', 'missingStart');
  const allDay = startDate !== '' && !startTime && !endTime;
  if (allDay && !notes.some((n) => n.code === 'missingStart')) note('assumed', 'assumedAllDay');
  if (!startDate) endDate = '';

  const title = text.title();
  if (!title) note('missing', 'missingTitle');

  // The same finding can be reached by two patterns: report it once.
  const unique = notes.filter(
    (item, index) =>
      notes.findIndex(
        (other) =>
          other.code === item.code && JSON.stringify(other.params) === JSON.stringify(item.params),
      ) === index,
  );
  notes.splice(0, notes.length, ...unique);
  const blocking = notes.some((n) => n.kind !== 'assumed');
  return {
    input,
    title,
    startDate,
    startTime,
    endDate,
    endTime,
    allDay,
    durationMinutes: duration,
    recurrence,
    notes,
    complete: !blocking && startDate !== '' && title !== '',
  };
}
